import { Injectable } from '@angular/core';
import { HttpService } from '../core/http.service';
import { GlobalsService } from '../globals.service';
import { Transfer, TransferFile, TransfersPage, TransfersPageParameters } from 'src/app/models/services/audience';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransfersFilesStatus } from 'src/app/models/services/transfersFilesStatus.enum';
import { IdentifiersTypes } from 'src/app/models/services/identifiersTypes.enum';
import { TransfersAttributes } from 'src/app/models/services/transfersAttributes.enum';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';
import { UsersService } from '../users.service';
import { TargetPlatform } from 'src/app/models/services/audiences/target-platform';
import { TransferFileAction } from 'src/app/models/services/transferFileAction.enum';

@Injectable({
  providedIn: 'root'
})
export class TransfersService {

  constructor(
    private httpService: HttpService,
    private globalsService: GlobalsService,
    private usersService: UsersService
  ) { }


  public createTransfer(transfer: Transfer): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${transfer.audience.id}/transfers`;
    return this.httpService.postNoResponse(url, transfer, this.globalsService.getActiveCompany()?.id);
  }

  public updateTransfer(transfer: Transfer): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${transfer.audience.id}/transfers/${transfer.id}`;
    return this.httpService.putNoResponse(url, transfer, this.globalsService.getActiveCompany()?.id);
  }

  public deleteTransfer(transfer: Transfer): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${transfer.audience.id}/transfers/${transfer.id}`;
    return this.httpService.deleteNoResponse(url, this.globalsService.getActiveCompany()?.id);
  }
  
  public getTransfersPage(pageParameters: TransfersPageParameters): Observable<TransfersPage> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/all/transfers/page`;
    return this.httpService.post(url, pageParameters, this.globalsService.getActiveCompany()?.id, true);
  }

  public sendTransfer(transfer: Transfer): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${transfer.audience.id}/transfers/${transfer.id}/send`;
    return this.httpService.postNoResponse(url, transfer, this.globalsService.getActiveCompany()?.id);
  }

  public createTransferFile(transfer: Transfer): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${transfer.audience.id}/transfers/${transfer.id}/file`;
    return this.httpService.postNoResponse(url, transfer, this.globalsService.getActiveCompany()?.id);
  }

  public downloadTransferFile(audienceId: number, transferId: number, audienceFileId: number): Observable<Blob> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/transfers/${transferId}/file/${audienceFileId}`;
    return this.httpService.getFile(url, this.globalsService.getActiveCompany()?.id);
  }

  public removeTransferFile(audienceId: number, transferId: number, audienceFileId: number): Observable<TransferFile[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/transfers/${transferId}/file/${audienceFileId}`;
    return this.httpService.delete(url, this.globalsService.getActiveCompany()?.id);
  }

  public sendTransferFile(audienceId: number, transferId: number, audienceFileId: number): Observable<void> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/${audienceId}/transfers/${transferId}/file/${audienceFileId}/send`;
    return this.httpService.postNoResponse(url, null, this.globalsService.getActiveCompany()?.id);
  }

  public getTargetPlatforms(): Observable<TargetPlatform[]> {
    let url: string = `${environment.audiencesServicesApiBaseUri}/audiences/transfers/target-platforms`;
    return this.httpService.get(url, this.globalsService.getActiveCompany()?.id);
  }  

  public getTargetPlatformIcon = (transfer: Transfer): string => {    
    return `assets/images/target-platforms/${transfer.targetPlatform}.png`;
  }

  public getFileStatusClass = (audienceFile: TransferFile): string => {
    switch (audienceFile?.status) {
      case TransfersFilesStatus.Creating:
      case TransfersFilesStatus.Sending:
      case TransfersFilesStatus.Sharing:
      case TransfersFilesStatus.WaitingCreationWindow:
        return "fa fa-hourglass-o fa-pulse";
      case TransfersFilesStatus.CreationError:
      case TransfersFilesStatus.SentError:
      case TransfersFilesStatus.SharingError:
        return "fa fa-exclamation-circle";
      default:
        return "fa fa-check-circle";
    }    

  }

  public getFileStatusLabel = (audienceFile: TransferFile): string => {
    switch (audienceFile?.status) {
      case TransfersFilesStatus.Creating:
        return `Creating (${audienceFile?.progress}%)`;
      case TransfersFilesStatus.Sending:
        return `Sending (${audienceFile?.progress}%)`;
      case TransfersFilesStatus.CreationError:
        return "Creation error";
      case TransfersFilesStatus.SentError:
        return "Sent error";
      case TransfersFilesStatus.Sharing:
        return "Sharing";
      case TransfersFilesStatus.SharingError:
        return "Sharing error";
      case TransfersFilesStatus.WaitingCreationWindow:
        return "Waiting for creation window";
      default:
        return "Done";
    }
  }  

  public getIdGraphTypeDescription = (identifierType: number) => {
    switch (identifierType) {
      case IdentifiersTypes.HashedEmail:
        return "Hashed emails";
      case IdentifiersTypes.WAMCookie:
        return "WAM cookies (encrypted)";
      case IdentifiersTypes.WAMCookie:
        return "WAM cookies";
      case IdentifiersTypes.MAID:
        return "MAIDs";
      case IdentifiersTypes.XandrCookie:
        return "Xandr cookies";
      case IdentifiersTypes.TTDCookie:
        return "TheTradeDesk cookies";       
      case -1:
        return "Home ids";              
      default:
        return "Unknown";
    };  
  }  

  public getTransferAttributeDescription = (attribute: TransfersAttributes, targetPlatform: TargetPlatforms) : string => {
    switch(attribute) {
      case TransfersAttributes.CustomSegmentId:
        return "Custom segment id";
      case TransfersAttributes.ZipPassword:
        return "Zip password";
      case TransfersAttributes.CustomSegmentDescription:
        return "Custom segment name";
      case TransfersAttributes.AudienceExternalIdentifier:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Amazon audience id";
          case TargetPlatforms.Meta:
            return "Meta audience id";
          case TargetPlatforms.WAMProxy:
            return "WAM audience id";    
          default:
            return "External audience identifier";                      
        }; 
      case TransfersAttributes.AudienceExternalName:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Amazon audience name";
          case TargetPlatforms.Meta:
            return "Meta audience name";
          case TargetPlatforms.WAMProxy:
            return "WAM audience name";    
          default:
            return "External audience name";                      
        };
      case TransfersAttributes.AudienceExternalSubType:
        switch(targetPlatform) 
        {
          case TargetPlatforms.TikTok:
            return "External TikTok sub type audience";   
          default:
            return "External sub type audience";                      
        };
      case TransfersAttributes.ProfileExternalIdentifier:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Amazon profile id";
          case TargetPlatforms.Meta:
            return "Meta business profile id";
          case TargetPlatforms.TheTradeDesk:
            return "TTD Partner id";  
          default:
            return "External profile identifier";                      
        };                 
      case TransfersAttributes.AdvertiserExternalIdentifier:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Amazon advertiser id";
          case TargetPlatforms.Meta:
            return "Meta ad account id";
          default:
            return "External advertiser identifier";                      
        };               
      case TransfersAttributes.AudienceExternalCategoryName:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Dataset name";
          default:
            return "External audience category name";                      
        };
      case TransfersAttributes.FirstPartyData:
        return "Is first party data";
      case TransfersAttributes.CPMRateAmount:
        return "CPM Rate amount ($)";
      case TransfersAttributes.PercentOfMediaCostRate:
        return "Percent of media cost rate";
      case TransfersAttributes.AdvertiserExternalIdentifier:
        switch(targetPlatform) 
        {
          case TargetPlatforms.Amazon:
            return "Amazon advertiser id";
          case TargetPlatforms.Meta:
            return "Meta ad account id";
          default:
            return "External advertiser identifier";                      
        };
      case TransfersAttributes.DatasetId:
        return "Amazon Dataset id";
      case TransfersAttributes.BacthId:
        return "BacthId";
      default:
        return "Missing attribute description";

    };
  }

  public isTransferFileActionAllowed = (action: TransferFileAction, transfer: Transfer, file?: TransferFile): boolean => {
    if (!transfer || !this.usersService.hasSelectedCompanyActionRight('Audiences', 'AudiencesTransfers', 'Manage')) {
        return false;
    }

    const platform = transfer.targetPlatform;

    switch (action) {
        case TransferFileAction.Download:
          return !!file && file.status === TransfersFilesStatus.Done && ![TargetPlatforms.Amazon, TargetPlatforms.WAMProxy].includes(platform);

        case TransferFileAction.CreateAndSend:
            const allowedPlatforms = [
                TargetPlatforms.WAMProxy,
                TargetPlatforms.Meta,
                TargetPlatforms.TheTradeDesk,
                TargetPlatforms.TikTok,
                TargetPlatforms.Amazon,
                TargetPlatforms.DV360
            ];

            const adminPlatforms = [
                TargetPlatforms.TapTap,
                TargetPlatforms.TapTapOOH,
                TargetPlatforms.Movistar,
                TargetPlatforms.A3Media,
                TargetPlatforms.Xandr
            ];

            return allowedPlatforms.includes(platform) || (this.usersService.hasGlobalSectionRight('Admin') && adminPlatforms.includes(platform));

        case TransferFileAction.Send:
          if ([TargetPlatforms.Amazon, TargetPlatforms.WAMProxy].includes(platform)) {
            return false;
          }

          if (platform === TargetPlatforms.TikTok) {
            const attrs = transfer.customAttributes ?? [];
            if (attrs.length === 1 && attrs[0]?.attributeId === 13) {
                return false;
            }
          }

          return file?.status === TransfersFilesStatus.Done;

        case TransferFileAction.Delete:
          return !!file &&
            ![TargetPlatforms.Amazon, TargetPlatforms.TikTok].includes(platform) &&
            ![TransfersFilesStatus.Creating, TransfersFilesStatus.Sending].includes(file.status);
        
        case TransferFileAction.Share:
          return [TargetPlatforms.Amazon, TargetPlatforms.Meta, TargetPlatforms.TikTok].includes(platform);

        default:
          return false;
    }
  }

}
